/**
 * Add Corporate Entity Panel — slide-over form for manually adding an entity
 * to the corporate structure (director, shareholder, related company, …).
 *
 * The new row is written to `corporateEntries` (via `addManualCorporateEntry`)
 * with `isManualOverride: true`, then reconciled so it also surfaces in the
 * job's Contacts list. On success we also trigger `onAdded` (the parent's
 * agent re-run) so the AI narrative summary is refreshed to include the new
 * entity. Manual (`isManualOverride`) rows are protected from agent edits, so
 * the re-run enriches around them rather than overwriting them.
 */

import { useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ScrollArea } from "@/ui/primitives/scroll-area";

type EntityType = "company" | "person" | "trust" | "organisation";
type EntityCategory =
  | "officers"
  | "shareholders"
  | "related_companies"
  | "subsidiaries"
  | "trusts"
  | "secured_parties"
  | "professionals"
  | "other";

const ENTITY_TYPE_OPTIONS: ReadonlyArray<{ value: EntityType; label: string }> =
  [
    { value: "company", label: "Company" },
    { value: "person", label: "Person" },
    { value: "trust", label: "Trust" },
    { value: "organisation", label: "Organisation" },
  ];

const CATEGORY_OPTIONS: ReadonlyArray<{
  value: EntityCategory;
  label: string;
}> = [
  { value: "officers", label: "Officer (director / secretary)" },
  { value: "shareholders", label: "Shareholder" },
  { value: "related_companies", label: "Related company" },
  { value: "subsidiaries", label: "Subsidiary" },
  { value: "trusts", label: "Trust" },
  { value: "secured_parties", label: "Secured party" },
  { value: "professionals", label: "Professional adviser" },
  { value: "other", label: "Other" },
];

interface AddCorporateEntityPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  open: boolean;
  onClose: () => void;
  /**
   * Fired after a successful add. The parent uses this to re-run the corporate
   * agent so the AI narrative summary reflects the new entity (contacts are
   * already refreshed by the reconcile the mutation schedules).
   */
  onAdded?: () => void;
}

export function AddCorporateEntityPanel({
  jobId,
  taskId,
  open,
  onClose,
  onAdded,
}: AddCorporateEntityPanelProps) {
  const [entityName, setEntityName] = useState("");
  const [entityType, setEntityType] = useState<EntityType>("person");
  const [category, setCategory] = useState<EntityCategory>("officers");
  const [detail, setDetail] = useState("");
  const [acn, setAcn] = useState("");
  const [abn, setAbn] = useState("");
  const [status, setStatus] = useState<"current" | "former">("current");

  const entityNameId = useId();
  const entityTypeId = useId();
  const categoryId = useId();
  const detailId = useId();
  const acnId = useId();
  const abnId = useId();
  const statusId = useId();

  const addEntity = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.corporate.publicMutations
        .addManualCorporateEntry,
    ),
  });

  function resetForm() {
    setEntityName("");
    setEntityType("person");
    setCategory("officers");
    setDetail("");
    setAcn("");
    setAbn("");
    setStatus("current");
  }

  async function handleSubmit() {
    if (!entityName.trim()) return;
    try {
      await addEntity.mutateAsync({
        jobId,
        taskId,
        entityName: entityName.trim(),
        entityType,
        category,
        detail: detail.trim() || undefined,
        acn: acn.replace(/\s/g, "") || undefined,
        abn: abn.replace(/\s/g, "") || undefined,
        isCurrent: status === "current",
      });
      toast.success(
        "Entity added. Re-running the analysis to refresh the summary.",
      );
      resetForm();
      onClose();
      onAdded?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : undefined;
      toast.error(message || "Could not add entity.");
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm();
          onClose();
        }
      }}
    >
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md">
        <div className="px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold">Add Entity</SheetTitle>
            <SheetDescription>
              Manually add an entity to the corporate structure. It also appears
              in this job&apos;s Contacts.
            </SheetDescription>
          </SheetHeader>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-6 pb-6">
            {/* Entity Name */}
            <div>
              <label
                htmlFor={entityNameId}
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Entity Name *
              </label>
              <Input
                id={entityNameId}
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                placeholder="Jane Smith, Acme Holdings Pty Ltd, …"
                className="h-9 text-sm"
                autoFocus
              />
            </div>

            {/* Entity Type */}
            <div>
              <label
                htmlFor={entityTypeId}
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Type
              </label>
              <Select
                value={entityType}
                onValueChange={(v) => setEntityType(v as EntityType)}
              >
                <SelectTrigger id={entityTypeId} className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div>
              <label
                htmlFor={categoryId}
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Category
              </label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as EntityCategory)}
              >
                <SelectTrigger id={categoryId} className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Detail */}
            <div>
              <label
                htmlFor={detailId}
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Detail
              </label>
              <Input
                id={detailId}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="e.g. Director, 50% shareholder"
                className="h-9 text-sm"
              />
            </div>

            {/* ACN / ABN */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor={acnId}
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  ACN
                </label>
                <Input
                  id={acnId}
                  value={acn}
                  onChange={(e) => setAcn(e.target.value)}
                  placeholder="9 digits"
                  className="h-9 font-mono text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor={abnId}
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  ABN
                </label>
                <Input
                  id={abnId}
                  value={abn}
                  onChange={(e) => setAbn(e.target.value)}
                  placeholder="11 digits"
                  className="h-9 font-mono text-sm"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label
                htmlFor={statusId}
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Status
              </label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as "current" | "former")}
              >
                <SelectTrigger id={statusId} className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="former">Former</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Submit */}
            <Button
              className="w-full"
              disabled={!entityName.trim() || addEntity.isPending}
              onClick={handleSubmit}
            >
              {addEntity.isPending ? "Adding..." : "Add Entity"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
